import React, {
    useState,
    useEffect,
    useRef,
    createContext,
    useContext,
} from "react";

import LoadingSpinnerImage from "../LoadingSpinnerImage.js";

const GlobalAppContext = createContext();

// compact: hides a comment's date while its options (delete/edit) are open, and only shows "Save comment" once something is typed.
const CommentsList = ({ entityType, entityId, userList, currentUserId, sectionId, sectionLookup, compact = false }) => {

    const [addCommentText, setAddCommentText] = useState("");
    const [isLoadingObjects, setIsLoadingObjects] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(2);
    const [errorAddCommentText, setErrorAddCommentText] = useState(null);
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [errorAddingComment, setErrorAddingComment] = useState(false);
    const [editedComment, setEditedComment] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentsPage, setCommentsPage] = useState(0);
    const [hasMoreComments, setHasMoreComments] = useState(true);
    const [apiURL, setApiURL] = useState()

    const useDummy = false;


    const validFormField = (field, errorFunc) => {
        if (field == "") {
            errorFunc(true);
            return false;
        }
        errorFunc(false);
        return true;
    };


    /*
    Convert: Wed Aug 30 2023 00:00:00 GMT+0100 (Western European Summer Time) 
    To: 2023-08-30
    */

    const formatDate = (dateStr, includeTime = false) => {
        const dateObj = new Date(dateStr);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Month is zero-indexed
        const day = String(dateObj.getDate()).padStart(2, "0");

        let time = "";
        if (includeTime) {
            let hours = (dateObj.getHours() < 10 ? '0' : '') + dateObj.getHours();
            let minutes = (dateObj.getMinutes() < 10 ? '0' : '') + dateObj.getMinutes();

            time = ` ${hours}:${minutes}`;
        }

        const formattedDate = `${year}-${month}-${day}${time}`;
        return formattedDate;
    };


    const saveComment = () => {
        //console.log("comment for EntityType: " + entityType + " entityId " + entityId + ": " + addCommentText);

        let addCommentURL = apiURL;

        let newComment = {
            Text: addCommentText,
            EntityId: entityId,
            EntityType: entityType
        };

        let entityFieldName;

        if (entityType == "product-skus")
            entityFieldName = "productId";
        else if (entityType == "countryregistration-certificate")
            addCommentURL += "comment=" + encodeURIComponent(addCommentText);
        else if (entityType == "countryregistration-operator")
            addCommentURL += "comment=" + encodeURIComponent(addCommentText);
        else
            entityFieldName = "complianceId";
            

        newComment = { ...newComment, [entityFieldName]: entityId };
        if (sectionId != null) newComment = { ...newComment, SectionId: sectionId };


        let method = "POST";

        // Is it a comment edit?
        if (editedComment != null) {
            newComment["Id"] = editedComment.Id;
            method = "PUT";
        }

        const requestOptions = {
            method: method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newComment)
        };

        // Validations

        let isValidForm = true;

        setErrorAddCommentText(false);
        setErrorAddingComment(false);


        if (!validFormField(addCommentText, setErrorAddCommentText)) isValidForm = false;


        if (!isValidForm) return false;

        setIsAddingComment(true);

        fetch(addCommentURL, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));

                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((data) => {
                // Update state with the received data
                if (data.Id) {
                    let newCommentsList = [data, ...comments];
                    setComments(newCommentsList);
                    setAddCommentText("");
                    setErrorAddingComment(false);
                }
                else {
                    console.log("error: ");
                    console.log(data);
                    setErrorAddingComment(true);
                }
            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                console.log("error: " + error);
                setErrorAddingComment(true);
            })
            .finally(() => {
                setIsAddingComment(false);
            });


    }


    const deleteComment = (commentId) => {

        //console.log("Delete comment " + commentId);



        let deleteCommentURL =
            apiURL +
            `id=${encodeURIComponent(entityId)}&` + 
            `commentId=${encodeURIComponent(commentId)}&`;

        let method = "DELETE";

        const requestOptions = {
            method: method,
            headers: { "Content-Type": "application/json" },
        };


        fetch(deleteCommentURL, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));

                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((data) => {
                if (data) {
                    let newCommentsList = comments.filter((item) => item.Id != commentId);
                    setComments(newCommentsList);
                }
            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                console.log("error: " + error);
            })
            .finally(() => {

            });


    }


    const editComment = (commentId) => {

        let comment = comments.find((item) => item.Id == commentId);

        if (comment) {
            console.log("Edit comment " + commentId);
            console.log(comment);
            setEditedComment(comment);
            setAddCommentText(comment.Text);
        }
        else {
            console.log(`Comment ${commentId} not found`);
        }
    }


    /* SCROLLING BEHAVIOUR */

    const ref = useRef();
    const isVisible = useIsVisible(ref);

    function useIsVisible(ref) {
        const [isIntersecting, setIntersecting] = useState(false);

        useEffect(() => {
            const observer = new IntersectionObserver(([entry]) =>
                setIntersecting(entry.isIntersecting),
            );

            observer.observe(ref.current);
            return () => {
                observer.disconnect();
            };
        }, [ref]);

        //console.log("isVisible? ", isIntersecting);

        return isIntersecting;
    }


    useEffect(() => {
        if (isVisible && hasMoreComments && !isLoadingObjects) {
            loadObjects({ nextPage: true });
        }
    }, [isVisible]);


    useEffect(() => {

        if (entityType == "product-skus")
            setApiURL("/umbraco/api/RIMSProducts/Comments?");
        else if(entityType == "countryregistration-certificate")
            setApiURL("/umbraco/api/RIMSRegistrations/Comments?certificateId=" + entityId + "&");
        else if(entityType == "countryregistration-operator")
            setApiURL("/umbraco/api/RIMSRegistrations/Comments?operatorId=" + entityId + "&");
        else
            setApiURL("/umbraco/api/Compliance/Comment?");

    }, []);

    const loadObjects = ({
        nextPage = false,
    } = {}) => {

        if (isLoadingObjects) return;

        if (!hasMoreComments) return;

        setIsLoadingObjects(true);

        let local_objects = comments;
        let local_objects_page = commentsPage;

        if (nextPage == false) {
            local_objects = [];
            local_objects_page = 0;
            setComments(local_objects);
            setCommentsPage(local_objects_page);
            setHasMoreComments(true);
        } else {
            local_objects_page++;
            setCommentsPage(local_objects_page);
        }


        let loadObjectsURL =
            apiURL +
            `entityId=${entityId}&` +
            `id=${entityId}&` +
            `entityType=${entityType}&` +
            `page=${encodeURIComponent(local_objects_page)}&` +
            (sectionId != null ? `sectionId=${sectionId}&` : "");

        let requestOptions = {};

        if (useDummy) {
            loadObjectsURL = "https://dummyjson.com/products/search?q=dummy_" + entityId + "_page_" + local_objects_page;
            requestOptions = {};
        }

        fetch(loadObjectsURL, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));

                    setHasMoreComments(false);
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((data) => {

                if (useDummy) {
                    data = [];
                    let returnNrItems = itemsPerPage;
                    if (local_objects.length >= 2) returnNrItems = 0;
                    for (let i = 0; i < returnNrItems; i++) {
                        let rnd = Math.round(Math.random() * 99999);
                        data.push({ Id: rnd, Text: "Hello this is a comment", CreatedBy: 71975, CreatedDate: "2023-11-16 10:56", Edited: Math.round(Math.random()) },);
                    }
                }


                if (data.length > 0) {
                    // Was it a fetch more request? Then append to objects.
                    if (nextPage) {
                        let res = [...local_objects, ...data];
                        setComments(res);
                    } else {
                        setComments(data);
                    }
                }
                else {
                    setHasMoreComments(false);
                }


            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                setHasMoreComments(false);
            })
            .finally(() => {
                setIsLoadingObjects(false);
            });
    };


    let globalContext = {};

    globalContext.userList = userList;
    globalContext.currentUserId = currentUserId;
    globalContext.formatDate = formatDate;
    globalContext.sectionLookup = sectionLookup;
    globalContext.compact = compact;

    return (<>


        <GlobalAppContext.Provider value={globalContext}>

            {/* Comment form */}
        
            <div style={{ display: "flex", flexDirection: "column", }}>
                <div style={{ display: "flex", flexDirection: "row", }}>
                    <div><User userId={currentUserId} /> {editedComment != null && editedComment.Edited == 1 ? "(edited)" : null}</div>
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", marginLeft: "10px", }}>

                        {editedComment != null ? <>
                            <div style={{ fontSize: "0.8em", color: "#999" }}>Editing comment: "<span style={{ fontStyle: "italic" }}>{comments.find((item) => item.Id == editedComment.Id)?.Text.substring(0, 100)}...</span>"</div>
                        </> : null}

                        <textarea className={`addCommentTextBox form-field-text ${errorAddCommentText ? "form-field-error" : ""}`}
                            onChange={(e) => setAddCommentText(e.target.value)}
                            placeholder="Add a comment"
                            value={addCommentText}
                        ></textarea>

                        {errorAddCommentText && (
                            <span className="form-field-error-message">
                                The field Comment is necessary
                            </span>
                        )}

                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginTop: "10px", }}>

                            {isAddingComment ? <>

                                <div>
                                    <LoadingSpinnerImage />
                                </div>

                            </> : <>

                                {(!compact || addCommentText?.trim()) && <div>
                                    <button onClick={saveComment} className="action-button save-button">Save comment</button>
                                </div>}

                                {errorAddingComment && <div>
                                    An error occurred...
                                </div>}

                            </>}

                        </div>
                    </div>
                </div>
            </div>


            <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>


            <div className="thin-scrollbar" style={{ maxHeight: "100vh", overflow: "auto", paddingRight: "10px", }}>

                {/* Comment list */}

                {comments.length > 0 && <>

                    {comments.map((comment) => <Comment key={comment.Id} comment={comment} deleteCommentFunc={deleteComment} editCommentFunc={editComment} />)}

                </>}

                {/* Loading and no-more-results row */}

                {isLoadingObjects ? (
                    <div style={{ textAlign: "center" }}>
                        <LoadingSpinnerImage />
                    </div>
                ) :
                    <>
                        {!hasMoreComments && (
                            <div className="no-more-results">{comments.length > 0 ? "No more comments" : "No comments"}</div>
                        )}
                    </>
                }

                <div ref={ref}></div>

            </div>

            <div style={{ minHeight: "20px" }}></div>


        </GlobalAppContext.Provider>

    </>);

};






const Comment = ({ comment, deleteCommentFunc, editCommentFunc }) => {

    const AppContext = useContext(GlobalAppContext);


    const [isShowingDeleteCommentButton, setIsShowingDeleteCommentButton] = useState(false);
    const [isShowingOptions, setIsShowingOptions] = useState(false);
    const [isDeletingComment, setIsDeletingComment] = useState(false);

    const showOptions = (status) => {
        setIsShowingDeleteCommentButton(false);
        setIsShowingOptions(status);
        setIsDeletingComment(false);
    }

    let userId = comment.CreatedBy;
    let user = AppContext.userList.find((item) => item.Id == userId);
    let commentText = comment.Text;
    let date = AppContext.formatDate(comment.CreatedDate, true);
    let commentId = comment.Id;
    let sectionLabel = comment.SectionId ? AppContext.sectionLookup?.[comment.SectionId] : null;

    return (<>

        <div style={{ display: "flex", flexDirection: "row", }}>
            <div><User userId={userId} /></div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1, marginLeft: "10px", }}>
                <div style={{ display: "flex", flexDirection: "row", width: "100%", }}>
                    <div style={{ flex: 1, fontWeight: "bold" }}>{user?.Name}</div>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        {/* Date */}
                        {!(AppContext.compact && isShowingOptions) && <div style={{ color: "#a8a8a8", marginRight: "5px" }}>{date}</div>}

                        {userId == AppContext.currentUserId ? <>

                            {/* Options */}
                            <div style={{ display: "flex", flexDirection: "row" }}>

                                {isShowingOptions ? <>

                                    {!isShowingDeleteCommentButton &&
                                        <div>
                                            <button onClick={() => setIsShowingDeleteCommentButton(true)} className="action-button options-button" title="Delete comment"><i className="fas fa-trash-alt"></i></button>
                                        </div>
                                    }

                                    {isShowingDeleteCommentButton &&
                                        <>
                                            {isDeletingComment ? <>
                                                <div>
                                                    <LoadingSpinnerImage />
                                                </div>
                                            </> : <>
                                                <div>
                                                    <button onClick={() => { setIsDeletingComment(true); deleteCommentFunc(commentId); }} className="action-button delete-button" title="Confirm deletion"><i className="fas fa-trash-alt"></i> Yes, delete</button>
                                                </div>
                                            </>}
                                        </>
                                    }

                                    {/*
                                <div>
                                    <button onClick={() => editCommentFunc(commentId)} className="action-button options-button" title="Edit comment"><i className="fas fa-pencil-alt"></i></button>
                                </div>
                                */}
                                    <div>
                                        <button onClick={() => showOptions(false)} className="action-button options-button" title="Cancel">Cancel</button>
                                    </div>
                                </> : <>
                                    {
                                        <div>
                                            <button onClick={() => showOptions(true)} className="action-button options-button" title="Show options"><i className="fas fa-ellipsis-v"></i></button>
                                        </div>
                                    }
                                </>}

                            </div>

                        </> : <>

                            {/* Empty space for when the user is not the owner of the comment to align with the rest of the dates */}
                            <div style={{ width: "40px" }}></div>

                        </>}

                    </div>
                </div>
                {sectionLabel && (
                    <div style={{ fontSize: "0.8em", color: "#999", marginBottom: "2px" }}>
                        <i className="fas fa-sitemap" style={{ marginRight: "4px" }}></i>
                        on {sectionLabel}
                    </div>
                )}
                <div>{commentText}</div>
            </div>
        </div>

        <div style={{ marginTop: "20px", borderTop: "1px solid #f3f3f3", marginBottom: "20px" }}></div>

    </>);

};



const User = ({ userId, size }) => {

    const AppContext = useContext(GlobalAppContext);

    let user = AppContext.userList.find((item) => item.Id == userId);

    return (
        <>
            {user ? (
                <div
                    className={
                        "user-initials-circle " +
                        (size == "small" ? "user-initials-circle-small" : "")
                    }
                    style={{ backgroundColor: user?.IsItMe ? "#0176b9" : "#e7e7e7", color: user?.IsItMe ? "white" : "black" }}
                    title={user?.Name}
                >
                    {user?.Initials}
                </div>
            ) : null}
        </>
    );
};



export default CommentsList;